/**
 * Privacy Policy Page Component
 * 
 * Comprehensive privacy policy page for Money Mojo, outlining how we collect,
 * use, and protect user data in accordance with Australian Privacy Act 1988,
 * GDPR, and CCPA requirements.
 * 
 * Features:
 * - Complete privacy policy content tailored for financial management app
 * - Dark mode support
 * - Responsive design
 * - Accessibility compliance
 * - Breadcrumb navigation for authenticated users
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';

export function PrivacyPolicyPage(): React.ReactElement {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setIsAuthenticated(!!session);
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
      setIsLoading(false);
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleBack = () => {
    // Navigate back to previous page or dashboard
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="space-y-8">
      {/* Breadcrumb for authenticated users */}
      {!isLoading && isAuthenticated && (
        <nav aria-label="Breadcrumb" className="flex items-center space-x-2 text-sm">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors focus-ring rounded"
            aria-label="Go back"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            <span>Back</span>
          </button>
          <span className="text-gray-400 dark:text-gray-500" aria-hidden="true">&lt;</span>
          <span className="text-gray-900 dark:text-gray-100 font-medium">Privacy Policy</span>
        </nav>
      )}

      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 scroll-mt-20">
          Privacy Policy
        </h1>
        <p className="mt-3 text-lg text-gray-600 dark:text-gray-400">
          How we protect your data and privacy
        </p>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-500">
          We're committed to transparency about how we collect, use, and protect your information. Your privacy is important to us.
        </p>
      </div>

      {/* Main Content */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 md:p-8 lg:p-10">
        <article className="prose prose-gray dark:prose-invert max-w-none">
          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              1. Introduction
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              This Privacy Policy outlines how Money Mojo ("we", "our", "us") collects, uses, and stores personal information when you access our financial management platform at MoneyMojo.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We are committed to protecting your privacy in accordance with the Privacy Act 1988 (Cth), the Australian Privacy Principles (APPs), and applicable global standards like the General Data Protection Regulation (GDPR) and California Consumer Privacy Act (CCPA).
            </p>
          </section>

          {/* What Information We Collect */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              2. What Information We Collect
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We only collect the minimum amount of personal information necessary to operate and improve the experience.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              This may include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
              <li>Account information (name, email address, profile settings)</li>
              <li>Financial transaction data (amounts, dates, descriptions, categories)</li>
              <li>Account details (bank accounts, account names, balances)</li>
              <li>Budget and goal information</li>
              <li>User preferences and settings</li>
              <li>Responses to AI categorisation prompts and corrections</li>
              <li>Browser type, device type, and IP address (via analytics)</li>
              <li>Usage data to improve our services</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We do not knowingly collect personal data from users under 16 without verified parental consent.
            </p>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              3. How We Use Your Information
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We use this information to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
              <li>Provide you with personalised financial management services</li>
              <li>Process and categorise your transactions using AI-powered tools</li>
              <li>Generate financial insights, reports, and analytics</li>
              <li>Manage your budgets, goals, and financial accounts</li>
              <li>Send important account notifications and updates (if you consent)</li>
              <li>Improve the performance and content of our platform</li>
              <li>Conduct basic analytics to understand aggregate user behaviour</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We do not use your data for advertising or profiling. Your financial data is used solely to provide you with financial management services.
            </p>
          </section>

          {/* Legal Basis for Processing */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              4. Legal Basis for Processing (for EU/UK residents)
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              If you are located in the European Economic Area or UK, we only process your data when we have a lawful basis:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
              <li><strong>Consent</strong> — e.g., when you submit your email to receive notifications or create an account</li>
              <li><strong>Legitimate interest</strong> — e.g., improving the platform's functionality and providing financial management services</li>
              <li><strong>Legal obligation</strong> — e.g., complying with regulatory requests or financial reporting requirements</li>
              <li><strong>Contract performance</strong> — e.g., processing transactions and providing the services you've requested</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              You can withdraw your consent at any time by contacting us or adjusting your privacy settings in your account.
            </p>
          </section>

          {/* Data Storage and Retention */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              5. Data Storage and Retention
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              Your financial data and account information are stored securely on our database infrastructure provided by Supabase, which uses industry-standard encryption and security measures.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              Local storage is used to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
              <li>Store your user preferences and settings</li>
              <li>Maintain your session state</li>
              <li>Cache data for improved performance</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              Local storage is only accessible on your device and not shared with third parties.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We retain your data for as long as your account is active or as needed to provide services. You can request deletion of your data at any time. We may retain certain information as required by law or for legitimate business purposes (e.g., transaction records for tax compliance).
            </p>
          </section>

          {/* Tracking Technologies */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              6. Tracking Technologies
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We do not use cookies for tracking purposes. However, we use local storage and similar browser technologies to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
              <li>Store your preferences and settings</li>
              <li>Maintain your session state</li>
              <li>Help pre-fill your information if you return</li>
              <li>Maintain a smoother user experience</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              You can manage or delete local storage via your browser settings.
            </p>
          </section>

          {/* Data Security */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              7. Data Security
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We take reasonable steps to protect your personal and financial data, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
              <li>HTTPS encryption (SSL/TLS) for secure data transmission</li>
              <li>Database encryption at rest</li>
              <li>Row-Level Security (RLS) policies to ensure users can only access their own data</li>
              <li>Access controls and strong authentication for admin access</li>
              <li>Regular security audits and updates</li>
              <li>Secure infrastructure provided by Supabase with SOC 2 compliance</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              This is why we keep data collection to a minimum — only capturing what's necessary to deliver financial management services and improve your experience. However, no method of electronic transmission is ever 100% secure.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We recommend using strong, unique passwords and enabling two-factor authentication when available.
            </p>
          </section>

          {/* Disclosure to Third Parties */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              8. Disclosure to Third Parties
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We do not sell or rent your personal or financial data.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We may share data with trusted service providers strictly for the purpose of operating the platform:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
              <li><strong>Supabase</strong> — Our secure backend infrastructure provider (database, authentication, storage)</li>
              <li><strong>Email platforms</strong> — For sending account notifications and updates (only if you consent)</li>
              <li><strong>Analytics tools</strong> — For understanding aggregate usage patterns (anonymised data only)</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              All third parties are required to comply with relevant data protection laws and are bound by confidentiality agreements. We do not share your financial transaction data with third parties except as necessary to provide the service.
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              9. Your Rights
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              Depending on your location, you may have the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
              <li>Access your personal data</li>
              <li>Correct or update inaccurate information</li>
              <li>Delete your data (subject to legal retention requirements)</li>
              <li>Export your data in a portable format</li>
              <li>Withdraw your consent</li>
              <li>Object to certain processing activities</li>
              <li>Restrict processing of your data</li>
              <li>Lodge a complaint with your data protection authority</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              To exercise any of these rights, please contact us using the information provided at the end of this policy, or use the privacy settings in your account.
            </p>
          </section>

          {/* International Data Transfers */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              10. International Data Transfers
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              As an Australian business, we may transfer data to servers or service providers outside your home country (e.g., Supabase infrastructure may be hosted in various regions). We take reasonable steps to ensure that any overseas recipient protects your data in a way consistent with this policy and applicable data protection laws.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Where data is transferred internationally, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses (SCCs) or adequacy decisions.
            </p>
          </section>

          {/* Updates to This Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              11. Updates to This Policy
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. The most recent version will always be available at this page. We will notify you of any material changes by posting the updated policy and updating the "Last Updated" date. Your continued use of the platform means you accept any changes.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              12. Contact Us
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us:
            </p>
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                <strong>Email:</strong> admin@moneymojo.finance
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                <strong>Address:</strong> Money Mojo Privacy Team, Australia
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                You can also manage your privacy settings directly in your account under Settings → Security & Privacy.
              </p>
            </div>
          </section>

          {/* Last Updated */}
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </article>
      </div>
    </div>
  );
}

